<template>
  <section class="cstcloud-page">
    <article class="cstcloud-card cstcloud-result-card" aria-live="polite">
      <div class="cstcloud-brand"><span>CST</span> 中国科技云</div>
      <div v-if="loading" class="cstcloud-spinner" aria-hidden="true" />
      <h1>{{ title }}</h1>
      <p>{{ message }}</p>
      <div v-if="!loading" class="cstcloud-actions">
        <button type="button" class="primary-action" @click="returnToLogin">返回登录</button>
        <button type="button" class="ghost-action" @click="front.goPath('/')">返回首页</button>
      </div>
    </article>
  </section>
</template>

<script>
import { consumeCstCloudLogin, saveToken } from '../../api/paleo'
import {
  normalizeCstCloudOutcome,
  safeRedirectPath,
  saveOnboardingFlow,
  unwrapApiData
} from '../../utils/cstCloudAuth'

export default {
  name: 'CstCloudResultPage',
  inject: ['front'],
  data() {
    return {
      loading: true,
      title: '正在确认科技云身份',
      message: '请稍候，不要重复刷新或返回授权页面。'
    }
  },
  created() {
    this.consumeTicket()
  },
  methods: {
    async consumeTicket() {
      const ticket = String(this.$route.query.ticket || '').trim()
      const callbackError = String(this.$route.query.error || '').trim()
      await this.$router.replace({ path: '/auth/cstcloud/result' }).catch(() => {})
      if (callbackError) {
        const cancelled = callbackError === 'authorization_cancelled'
        this.fail(cancelled ? '已取消科技云授权' : '科技云登录未完成',
          cancelled ? '您已取消本次授权，可返回后继续使用邮箱密码登录。' : '授权已失效或科技云服务暂不可用，请重新尝试。')
        return
      }
      if (!ticket) {
        this.fail('授权结果无效', '未找到有效的一次性票据，请重新发起科技云登录。')
        return
      }
      try {
        const data = unwrapApiData(await consumeCstCloudLogin(ticket))
        const outcome = normalizeCstCloudOutcome(data)
        if (data.token || ['SUCCESS', 'LOGIN_SUCCESS', 'AUTHENTICATED'].includes(outcome)) {
          await this.finishLogin(data)
          return
        }
        if (['BIND_SUCCESS', 'BOUND'].includes(outcome)) {
          this.loading = false
          this.title = '科技云账号已绑定'
          this.message = '绑定已完成，即将返回账户安全页面。'
          window.setTimeout(() => this.front.goPath('/personal-center?section=security'), 700)
          return
        }
        const flow = {
          onboardingTicket: data.onboardingTicket || data.ticket,
          profile: data.profile || data.identity || data.userInfo || {},
          redirectPath: safeRedirectPath(data.redirectPath || data.returnPath, '/'),
          emailExists: !!(data.emailExists || data.accountExists)
        }
        if (!flow.onboardingTicket) {
          throw new Error(data.message || data.msg || '科技云登录结果不完整，请重新尝试')
        }
        saveOnboardingFlow(flow)
        if (flow.emailExists || ['BIND_EXISTING', 'NEED_BIND_EXISTING', 'ACCOUNT_EXISTS'].includes(outcome)) {
          await this.$router.replace('/auth/cstcloud/bind-existing')
        } else {
          await this.$router.replace('/auth/cstcloud/onboarding')
        }
      } catch (error) {
        this.fail('科技云登录未完成', error.message || '授权已取消、超时或服务暂不可用，请重新登录。')
      }
    },
    async finishLogin(data) {
      if (!data.token) throw new Error('本站登录凭证签发失败，请重新尝试')
      saveToken(data.token)
      this.front.isLoggedIn = true
      this.front.applyMembershipSession(data)
      await this.front.loadSelfService()
      this.title = '登录成功'
      this.message = '身份确认完成，正在返回您之前访问的页面。'
      const target = safeRedirectPath(data.redirectPath || data.returnPath, '/')
      await this.$router.replace(target).catch(() => {})
    },
    fail(title, message) {
      this.loading = false
      this.title = title
      this.message = message
    },
    returnToLogin() {
      this.front.goPath('/')
      this.$nextTick(() => this.front.openAuth('login'))
    }
  }
}
</script>

<style scoped>
.cstcloud-result-card { max-width: 600px; text-align: center; }
.cstcloud-spinner { width: 44px; height: 44px; margin: 24px auto; border: 4px solid #d8e4ef; border-top-color: #0d5c91; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
